import {useEffect} from 'react';
import cx from 'classnames';
import {createPortal} from 'react-dom';
import {FocusTrap} from 'focus-trap-react';
import {Button} from '../atoms/Button';
import {Typography} from '../atoms/Typography';
import {CloseLineIcon} from '../svg/CloseLineIcon';
import {AlertLineIcon} from '../svg/AlertLineIcon';
import {feedbackErrorText} from '../sass/base/_exports.sass';
import {useIsLargishAndUp} from '../hooks/useMatchQuery';

/**
 * This component functions as a full-screen view on mobile, and an
 * adjustable-sized modal on desktop. It contains a configurable header
 * that can contain a title and buttons (or custom children) and a configurable
 * footer for action button (or custom children).
 *
 * The opening and closing uses animations which can be disabled.
 *
 * @param {object} props
 * @property {string|function} headerText null - Title for the modal.
 * @property {string} headerTextVariant 'header5' - The header typography variant.
 * @property {function} headerActions null - Components to render in top right of modal.
 * @property {boolean} showCloseButton true - Show the back arrow icon.
 * @property {string|function} footerActions null - If a string, renders a button with primaryAction trigger. If a component, renders it in footer instead.
 * @property {string} footerActionVariant 'primary' - Variant for primary button.
 * @property {function} primaryAction - If you used a string for `footerActions`, then this function will be called when clicked.
 * @property {function} onClose () => {} - Function to call when close is clicked.
 * // react-modal props
 * @property {boolean} isOpen false - Boolean describing if the modal should be shown or not when mounted.
 * @property {string} className '' - String className to be applied to the modal content.
 * @property {number} closeTimeoutMS 200 - Number indicating the milliseconds to wait before
 * @property {object} style null - Object indicating styles to be used for the modal.
 * @property {string} id '' - String id to be applied to the content div
 * @property {string} feedback null - String to show as feedback in the modal footer
 */

export const AdaptiveModal = ({
  bottomComponent = null,
  headerText = null,
  headerTextVariant = 'header5',
  headerActions = null,
  showCloseButton = true,
  primaryFooterAction = null,
  primaryFooterActionText = '',
  primaryFooterActionVariant = 'primary',
  primaryFooterActionDisabled = false,
  secondaryFooterAction = null,
  secondaryFooterActionVariant = 'secondary',
  secondaryFooterActionText = '',
  secondaryFooterActionDisabled = false,
  headerClassName = '',
  footerClassName = '',
  feedback = null,
  onClose = () => {},
  isOpen = false,
  className = '',
  id = '',
  children,
  focusTrapPaused = false,
}) => {
  const isLargishAndUp = useIsLargishAndUp();

  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => document.body.classList.add('no-scroll'), 100);
    } else {
      window.setTimeout(() => document.body.classList.remove('no-scroll'), 100);
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  return isOpen
    ? createPortal(
        <div
          id={id}
          className={cx('adaptive-modal', className, {
            'adaptive-modal__no-footer': !primaryFooterAction,
          })}
        >
          {isLargishAndUp && <div className="adaptive-modal__overlay" />}
          <FocusTrap active={isOpen} paused={focusTrapPaused}>
            <div className="adaptive-modal__content">
              <div className={cx('adaptive-modal__header', headerClassName)}>
                {headerText && (
                  <Typography className="adaptive-modal__header-title" variant={headerTextVariant}>
                    {headerText}
                  </Typography>
                )}
                {(headerActions || showCloseButton) && (
                  <div className="adaptive-modal__header-actions">
                    {headerActions}
                    {showCloseButton && (
                      <Button
                        variant="transparent"
                        data-qa-id="adaptive-modal-close-button"
                        className="adaptive-modal__action-button"
                        onClick={onClose}
                        icon={<CloseLineIcon ratio={0.75} />}
                        ariaLabel="Close modal"
                        disableProcessing
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="adaptive-modal__body">{children}</div>
              {bottomComponent && (
                <div className="adaptive-modal__bottom-component">{bottomComponent}</div>
              )}
              {primaryFooterAction && (
                <div className={cx('adaptive-modal__footer', footerClassName)}>
                  {isLargishAndUp && feedback && (
                    <Typography
                      className="adaptive-modal__footer-feedback"
                      variant="paragraph2"
                      color="feedbackErrorText"
                    >
                      <AlertLineIcon color={feedbackErrorText} ratio={0.75} />
                      {feedback}
                    </Typography>
                  )}
                  {secondaryFooterAction && (
                    <Button
                      variant={secondaryFooterActionVariant}
                      onClick={secondaryFooterAction}
                      disabled={secondaryFooterActionDisabled}
                      data-qa-id="adaptive-modal-secondary-action-button"
                      size="large"
                      fullWidth={!isLargishAndUp}
                    >
                      {secondaryFooterActionText}
                    </Button>
                  )}
                  {primaryFooterAction && (
                    <Button
                      className="adaptive-modal__primary-action"
                      variant={primaryFooterActionVariant}
                      onClick={primaryFooterAction}
                      disabled={primaryFooterActionDisabled}
                      data-qa-id="adaptive-modal-primary-action-button"
                      size="large"
                      fullWidth
                    >
                      {primaryFooterActionText}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </FocusTrap>
        </div>,
        document.body
      )
    : null;
};
